import {AfterViewInit, Component, effect, ElementRef, OnDestroy, OnInit, Signal, ViewChild} from '@angular/core';
import {Context} from '../../models/context';
import {ContextService} from "../../services/context/context-signal.service";
import {PageHeaderService} from "../../services/page-header/page-header.service";
import {Store} from '../../models/store';
import {StoreService} from '../../services/property/store.service';
import {StockService} from '../../services/stock/stock.service';
import {StockOperation} from '../../models/enums';
import {UiToastService} from '../../shared/ui';
import {faBarcode, faCamera} from '@fortawesome/free-solid-svg-icons';
import {AppLogger, AppLoggerService} from '../../core/logging';

type ScanRowStatus = 'sending' | 'added' | 'removed' | 'failed' | 'undone';

interface ScanFeedRow {
  key: string;
  code: string;
  operation: 'ADD' | 'REMOVE';
  status: ScanRowStatus;
  name?: string;
  brand?: string;
  thumb?: string;
  stockId?: number;
  expiry?: Date | null;
  error?: string;
}

@Component({
  selector: 'app-scan',
  templateUrl: './scan.component.html',
  styleUrls: ['./scan.component.scss'],
  standalone: false
})
export class ScanComponent implements AfterViewInit, OnInit, OnDestroy {

  private readonly log: AppLogger;

  context: Signal<Context> = this.contextService.context;

  // Icons
  faBarcode = faBarcode;
  faCamera = faCamera;

  // Enums for the template
  eStockOperation = StockOperation;

  // Context / stores
  stores: Store[] = [];
  ctxStoreId: number | null = null;
  private householdId: number | null = null;

  // Batch controls
  operation: StockOperation = StockOperation.Add;
  wedgeInput = '';

  // Sticky expiry (Add only)
  expiryEnabled = false;
  stickyExpiry: Date | null = null;

  // Camera
  cameraOn = false;

  // Live feed (newest first)
  feed: ScanFeedRow[] = [];

  @ViewChild('wedge') wedgeRef?: ElementRef<HTMLInputElement>;

  private keySeq = 0;

  constructor(
    private contextService: ContextService,
    private appLogger: AppLoggerService,
    private pageHeaderService: PageHeaderService,
    private storeService: StoreService,
    private stockService: StockService,
    private toastService: UiToastService,
  ) {
    this.log = this.appLogger.createForClass(ScanComponent);

    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;

      this.householdId = c.household?.id ?? null;

      if (this.householdId != null) {
        this.loadStores(this.householdId);
      } else {
        this.stores = [];
        this.ctxStoreId = null;
      }
    });
  }

  ngOnInit(): void {
    this.log.info('Initialize batch scan station');
    this.pageHeaderService.setPageTitle('Scan');
  }

  ngAfterViewInit(): void {
    this.focusWedge();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearAll();
  }

  // ---- Stores -------------------------------------------------------------

  private loadStores(householdId: number): void {
    this.storeService.stores(householdId).subscribe({
      next: (data: Store[]) => {
        this.stores = data ?? [];
        if (this.ctxStoreId == null) {
          const defaultStore = this.stores.find(s => s.is_default);
          this.ctxStoreId = defaultStore?.id ?? null;
        }
      },
      error: () => {
        this.stores = [];
        this.ctxStoreId = null;
      }
    });
  }

  onStoreSelected(store: Store): void {
    this.ctxStoreId = store?.id ?? null;
    this.focusWedge();
  }

  // ---- Controls -----------------------------------------------------------

  setOperation(operation: StockOperation): void {
    this.operation = operation;
    this.focusWedge();
  }

  onExpiryToggle(enabled: boolean): void {
    this.expiryEnabled = enabled;
    if (!enabled) {
      this.stickyExpiry = null;
    }
    this.focusWedge();
  }

  onExpirySelected(result: { date: Date | null }): void {
    this.stickyExpiry = result?.date ?? null;
    this.focusWedge();
  }

  toggleCamera(): void {
    this.cameraOn = !this.cameraOn;
    if (!this.cameraOn) {
      this.focusWedge();
    }
  }

  // ---- Scanning -----------------------------------------------------------

  /** Keyboard-wedge / manual submit (Enter). */
  onWedgeSubmit(): void {
    const code = (this.wedgeInput || '').trim();
    this.wedgeInput = '';
    if (!code) {
      this.focusWedge();
      return;
    }
    this.pushScan(code);
    this.focusWedge();
  }

  /** Continuous camera emits a code. */
  onCameraScan(code: string): void {
    const clean = (code || '').trim();
    if (!clean) return;
    this.pushScan(clean);
  }

  private pushScan(code: string): void {
    if (!this.ctxStoreId) {
      this.toastService.error('Select a store first');
      return;
    }

    const row: ScanFeedRow = {
      key: `row-${++this.keySeq}`,
      code,
      operation: this.operation === StockOperation.Add ? 'ADD' : 'REMOVE',
      status: 'sending',
      expiry: this.operation === StockOperation.Add && this.expiryEnabled ? this.stickyExpiry : null,
    };
    this.feed.unshift(row);
    this.dispatch(row);
  }

  private dispatch(row: ScanFeedRow): void {
    const storeId = this.ctxStoreId!;
    row.status = 'sending';
    row.error = undefined;

    if (row.operation === 'ADD') {
      const expiry = row.expiry ? new Date(row.expiry) : undefined;
      this.stockService.addProductToStockByCode(row.code, storeId, expiry).subscribe({
        next: (resp) => {
          const item = resp?.[0];
          row.status = 'added';
          row.stockId = item?.id;
          row.name = item?.product?.name;
          row.brand = item?.product?.brand;
          row.thumb = item?.product?.thumb_image_url;
        },
        error: (err) => {
          row.status = 'failed';
          row.error = this.errorText(err);
        }
      });
    } else {
      this.stockService.removeProductFromStock(row.code, storeId, 1).subscribe({
        next: () => {
          row.status = 'removed';
        },
        error: (err) => {
          row.status = 'failed';
          row.error = this.errorText(err);
        }
      });
    }
  }

  retry(row: ScanFeedRow): void {
    this.dispatch(row);
    this.focusWedge();
  }

  /** Undo a successful ADD by deleting the created stock item. */
  undo(row: ScanFeedRow): void {
    if (row.status !== 'added' || row.stockId == null) return;
    const stockId = row.stockId;
    this.stockService.removeById(stockId).subscribe({
      next: () => {
        row.status = 'undone';
      },
      error: (err) => {
        this.toastService.error(`Could not undo: ${this.errorText(err)}`);
      }
    });
    this.focusWedge();
  }

  clearFeed(): void {
    this.feed = [];
    this.focusWedge();
  }

  // ---- Session summary ----------------------------------------------------

  get addedCount(): number { return this.feed.filter(r => r.status === 'added').length; }
  get removedCount(): number { return this.feed.filter(r => r.status === 'removed').length; }
  get failedCount(): number { return this.feed.filter(r => r.status === 'failed').length; }

  // ---- Helpers ------------------------------------------------------------

  trackRow(_index: number, row: ScanFeedRow): string {
    return row.key;
  }

  rowInitial(row: ScanFeedRow): string {
    const src = (row.brand || row.name || row.code || '?').trim();
    return src.charAt(0).toUpperCase() || '?';
  }

  /** Deterministic tile color from the row key so tiles feel stable. */
  rowColor(row: ScanFeedRow): string {
    const palette = ['#12915A', '#E8480E', '#2563EB', '#B9760F', '#7C3AED', '#0E7490', '#BE185D'];
    let h = 0;
    for (let i = 0; i < row.key.length; i++) h = (h * 31 + row.key.charCodeAt(i)) | 0;
    return palette[Math.abs(h) % palette.length];
  }

  private errorText(err: any): string {
    return err?.error?.message || err?.message || 'Failed';
  }

  private focusWedge(): void {
    if (this.cameraOn) return;
    setTimeout(() => this.wedgeRef?.nativeElement?.focus(), 0);
  }
}
